import React, { useMemo } from 'react';
import {
  AttachmentProps,
  AudioContainer,
  CardContainer,
  FileContainer,
  GalleryContainer,
  ImageContainer,
  MediaContainer,
  UnsupportedAttachmentContainer,
  VoiceRecordingContainer,
} from 'stream-chat-react';
import type { Attachment } from 'stream-chat';
import { PdfAttachmentContainer } from './pdf-attachment-container';
import {
  isImageAttachment,
  isScrapedContent,
  isVideoAttachment,
  isAudioAttachment,
  isVoiceRecordingAttachment,
  isFileAttachment,
} from './stream-attachment-types';

const CONTAINER_MAP = {
  audio: AudioContainer,
  card: CardContainer,
  file: FileContainer,
  media: MediaContainer,
  unsupported: UnsupportedAttachmentContainer,
  voiceRecording: VoiceRecordingContainer,
  pdf: PdfAttachmentContainer,
} as const;

type GroupedRenderedAttachment = {
  [K in keyof typeof CONTAINER_MAP]: React.ReactNode[];
} & {
  gallery: React.ReactNode[];
  image: React.ReactNode[];
};

export const ATTACHMENT_GROUPS_ORDER = [
  'card',
  'gallery',
  'image',
  'media',
  'audio',
  'voiceRecording',
  'file',
  'unsupported',
  'pdf',
] as const;

export const CustomAttachments = (props: AttachmentProps) => {
  const { attachments } = props;

  const groupedAttachments = useMemo(() => renderGroupedAttachments(props), [attachments]);

  return (
    <div className="str-chat__attachment-list">
      {ATTACHMENT_GROUPS_ORDER.reduce(
        (acc, groupName) => [...acc, ...groupedAttachments[groupName]],
        [] as React.ReactNode[]
      )}
    </div>
  );
};

const renderGroupedAttachments = ({
  attachments,
  ...rest
}: AttachmentProps): GroupedRenderedAttachment => {
  const uploadedImages = attachments.filter((attachment) =>
    isImageAttachment(attachment as Attachment)
  );

  const containers = attachments
    .filter((attachment) => 'type' in attachment && attachment.type !== 'image')
    .reduce<GroupedRenderedAttachment>(
      (typeMap, attachment) => {
        const attachmentType = getAttachmentType(attachment as Attachment);

        const Container = CONTAINER_MAP[attachmentType];
        typeMap[attachmentType].push(
          <Container
            key={`${attachmentType}-${typeMap[attachmentType].length}`}
            {...rest}
            attachment={attachment}
          />
        );

        return typeMap;
      },
      {
        audio: [],
        card: [],
        file: [],
        media: [],
        unsupported: [],
        gallery: [],
        image: [],
        voiceRecording: [],
        pdf: [],
      }
    );

  if (uploadedImages.length > 1) {
    containers['gallery'] = [
      <GalleryContainer
        key="gallery-container"
        {...rest}
        attachment={{
          images: uploadedImages,
          type: 'gallery',
        }}
      />,
    ];
  } else if (uploadedImages.length === 1) {
    containers['image'] = [
      <ImageContainer key="image-container" {...rest} attachment={uploadedImages[0]} />,
    ];
  }

  return containers;
};

const getAttachmentType = (attachment: Attachment): keyof typeof CONTAINER_MAP => {
  if (isScrapedContent(attachment)) {
    return 'card';
  } else if (isVideoAttachment(attachment)) {
    return 'media';
  } else if (isAudioAttachment(attachment)) {
    return 'audio';
  } else if (isVoiceRecordingAttachment(attachment)) {
    return 'voiceRecording';
  } else if (isFileAttachment(attachment)) {
    if (attachment.mime_type === 'application/pdf' || attachment.title?.endsWith('.pdf')) {
      return 'pdf';
    }
    return 'file';
  }

  return 'unsupported';
};
